/**
 * The shell's own per-app rail permission loader. On every app switch the shell asks the
 * active app's `…/me/permissions` endpoint itself, so the rail never waits on (or is lost
 * with) a remote that mounts and unmounts — e.g. the redirect to the shell-owned /home.
 * Every map is keyed in the rail's own namespace for that app (`apps.ts` `permission`).
 */

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import {
  CRM_NAV_PERMISSION_ALIASES,
  rbacApi,
  resolvePayrollNavPermissions,
  usePermissionsStore,
} from '@clarix/rbac';
import { usePmsMyAccess } from '@clarix/services-pms';
import { useSupportMe } from '@clarix/services-support';
import type { AppDefinition, MeWorkspace, RailItem } from '@clarix/shared-types';
import { useMe } from '@/core/api';
import { CALENDAR_SHELL_PERM } from '@/gateway/calendar-permissions';
import { useCrmObjectsPending } from '@/core/api/crm-objects.api';
import { useCrmPermissions } from '@/core/api/crm-permissions.api';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';

export type RailPermissionStatus = 'loading' | 'ready' | 'error';

export interface RailPermissions {
  status: RailPermissionStatus;
  permissions: Record<string, boolean>;
}

type RailPermissionApp = Pick<AppDefinition, 'railItems' | 'moreItems'>;

/** Every distinct `permission` key the app's rail and More items are gated on. */
export function railPermissionKeys(app: RailPermissionApp): string[] {
  const keys = new Set<string>();
  for (const item of [...(app.railItems ?? []), ...(app.moreItems ?? [])]) {
    if (!item.permission) continue;
    for (const key of Array.isArray(item.permission) ? item.permission : [item.permission]) {
      keys.add(key);
    }
  }
  return [...keys];
}

/** Explicit booleans over the rail keys: an admin holds them all, anyone else the granted set. */
export function grantedRailPermissions(
  railKeys: readonly string[],
  granted: readonly string[] | null | undefined,
  isAdmin: boolean,
): Record<string, boolean> {
  // Shape-guarded: this runs in render, so a malformed response must read as "nothing granted".
  const held = new Set(Array.isArray(granted) ? granted : []);
  return Object.fromEntries(railKeys.map((k) => [k, isAdmin || held.has(k)]));
}

/** CRM rail items gate on `crm.*` aliases; the endpoint answers in `module.*` keys. */
export function crmRailPermissions(moduleMap: Record<string, boolean>): Record<string, boolean> {
  const resolved: Record<string, boolean> = { ...moduleMap };
  for (const [alias, moduleKeys] of Object.entries(CRM_NAV_PERMISSION_ALIASES)) {
    resolved[alias] = moduleKeys.some((k) => moduleMap[k] === true);
  }
  return resolved;
}

/** Workspace capability keys come from `/me`; its `module.*` keys from the projects matrix. */
export function workspaceRailPermissions(
  railKeys: readonly string[],
  membership: Pick<MeWorkspace, 'role' | 'admin_permissions'> | undefined,
  projectsPermissions: Record<string, boolean> = {},
): Record<string, boolean> {
  const capabilityKeys = railKeys.filter((k) => !k.startsWith('module.'));
  const resolved = grantedRailPermissions(
    capabilityKeys,
    membership?.admin_permissions,
    membership?.role === 'OWNER',
  );
  for (const key of railKeys) {
    if (!(key in resolved)) resolved[key] = projectsPermissions[key] === true;
  }
  return resolved;
}

/** HRMS answers an explicit boolean map over its catalog; anything not literally `true` is denied. */
export function hrmsRailPermissions(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, v === true]));
}

/** RBAC gate for one rail or More item: entitlement, then fail closed until the map is ready. */
export function railItemVisible(
  item: RailItem,
  rail: RailPermissions,
  disabledModules: ReadonlySet<string>,
): boolean {
  // Tenant ENTITLEMENT is a separate question from permission, and the only one that can
  // hide an ungated item (Calendar, Home). A deny-list: an empty set hides nothing.
  if (item.moduleCode && disabledModules.has(item.moduleCode)) return false;
  if (!item.permission) return true;
  if (rail.status !== 'ready') return false;
  const keys = Array.isArray(item.permission) ? item.permission : [item.permission];
  return item.permissionAny
    ? keys.some((k) => rail.permissions[k] === true)
    : keys.every((k) => rail.permissions[k] === true);
}

/** Apps with no package query hook to reuse; the others go through their package's hook. */
async function fetchRailPermissions(
  slug: string,
  workspaceSlug: string | null,
): Promise<Record<string, boolean>> {
  switch (slug) {
    case 'projects':
      return { ...(await rbacApi.getMyPermissions()).permissions };
    case 'payroll': {
      const res = await apiService.get<{ permissions: Record<string, boolean> }>(
        '/payroll/users/me/permissions',
      );
      return resolvePayrollNavPermissions(res.permissions ?? {});
    }
    case 'calendar': {
      // Workspace-scoped and a granted LIST; the hook turns it into explicit booleans.
      const res = await apiService.get<{ permissions?: unknown }>(
        `/${workspaceSlug}/calendar/me/permissions`,
      );
      const granted = Array.isArray(res?.permissions) ? res.permissions : [];
      return Object.fromEntries(
        granted.filter((k): k is string => typeof k === 'string').map((k) => [k, true]),
      );
    }
    case 'hrms': {
      const res = await apiService.get<{ permissions?: unknown }>(
        `/${workspaceSlug}/hrms/me/permissions`,
      );
      return hrmsRailPermissions(res?.permissions);
    }
    default:
      throw new Error(`No rail permission source for app "${slug}"`);
  }
}

const FETCHED_SLUGS = new Set(['projects', 'payroll', 'calendar', 'hrms']);
/** Apps whose `/me/permissions` lives under the workspace slug, so their map is keyed by it. */
const WORKSPACE_SCOPED_SLUGS = new Set(['calendar', 'hrms']);
/** Calendar keys the shell gates on beyond its rail items (Quick Meeting, panel rows). */
const CALENDAR_SHELL_KEYS: readonly string[] = Object.values(CALENDAR_SHELL_PERM);
const NO_APP: RailPermissions = { status: 'ready', permissions: {} };
const LOADING: RailPermissions = { status: 'loading', permissions: {} };

/** Stale data stays `ready` through a failed background refetch; only a first load is `loading`. */
function fromQuery<T>(
  query: { data: T | undefined | null; isError: boolean },
  toPermissions: (data: T) => Record<string, boolean>,
): RailPermissions {
  if (query.data != null) return { status: 'ready', permissions: toPermissions(query.data) };
  return query.isError ? { status: 'error', permissions: {} } : LOADING;
}

/** The active app's rail permission map, fetched by the shell on every app switch. */
export function useAppRailPermissions(app: AppDefinition | undefined): RailPermissions {
  const slug = app?.slug;
  const hasToken = useAuthStore((s) => !!s.token);
  const userId = useAuthStore((s) => s.user?.sub ?? null);
  const railKeys = useMemo(() => (app ? railPermissionKeys(app) : []), [app]);

  // Workspace's `module.*` rail keys belong to the projects matrix, so it reads that map too.
  const fetchedSlug = slug === 'workspace' ? 'projects' : slug;
  const activeWorkspaceSlug = useActiveWorkspaceSlug();
  const workspaceScoped = !!fetchedSlug && WORKSPACE_SCOPED_SLUGS.has(fetchedSlug);
  const fetchedWorkspace = workspaceScoped ? (activeWorkspaceSlug ?? null) : null;
  // Keyed by user so a new sign-in never reuses the previous user's map.
  const fetched = useQuery({
    queryKey: ['shell', 'rail-permissions', fetchedSlug, userId, fetchedWorkspace],
    queryFn: () => fetchRailPermissions(fetchedSlug ?? '', fetchedWorkspace),
    enabled:
      hasToken &&
      !!fetchedSlug &&
      FETCHED_SLUGS.has(fetchedSlug) &&
      (!workspaceScoped || fetchedWorkspace != null),
    staleTime: 60_000,
    retry: false,
  });
  // The package's query definitions, but in the shell's own QueryClient: each remote
  // mounts its own, so live changes reach this map only through the divergence check below.
  const support = useSupportMe({ enabled: hasToken && slug === 'support' });
  const pms = usePmsMyAccess({ enabled: hasToken && slug === 'pms' });
  const crm = useCrmPermissions(slug === 'crm');
  // The CRM rail also drops deactivated objects and adds custom ones once this resolves.
  const crmObjectsPending = useCrmObjectsPending(slug === 'crm');
  // Same key as AppShellLayout / WorkspaceShell, so this reads their cached /me.
  const me = useMe({ activeSlug: activeWorkspaceSlug ?? undefined });

  let result: RailPermissions;
  let refetch: () => void = () => {};
  if (!slug) {
    result = NO_APP;
  } else if (slug === 'crm') {
    const perms = fromQuery({ data: crm.map, isError: crm.isError }, crmRailPermissions);
    result = perms.status === 'ready' && crmObjectsPending ? LOADING : perms;
    refetch = crm.refetch;
  } else if (slug === 'support') {
    result = fromQuery(support, (d) =>
      grantedRailPermissions(railKeys, d.permissions, d.is_admin === true),
    );
    refetch = () => void support.refetch();
  } else if (slug === 'pms') {
    result = fromQuery(pms, (d) =>
      grantedRailPermissions(railKeys, d.permissions, d.is_super_admin === true),
    );
    refetch = () => void pms.refetch();
  } else if (slug === 'workspace') {
    const membership = me.data?.workspaces.find((w) => w.slug === activeWorkspaceSlug);
    const projectsPending = fetched.isPending && fetched.fetchStatus !== 'idle';
    // A failed projects fetch only leaves the `module.*` items hidden.
    const perms = fromQuery(me, () => workspaceRailPermissions(railKeys, membership, fetched.data));
    result = perms.status === 'ready' && projectsPending ? LOADING : perms;
    refetch = () => {
      void me.refetch();
      void fetched.refetch();
    };
  } else if (slug === 'calendar') {
    // `is_admin` only informs calendar's UI; the effective list is the whole answer. The
    // shell's own keys are added so a gate never depends on some rail item carrying them.
    const keys = [...new Set([...railKeys, ...CALENDAR_SHELL_KEYS])];
    result = fromQuery(fetched, (d) => grantedRailPermissions(keys, Object.keys(d), false));
    refetch = () => void fetched.refetch();
  } else if (FETCHED_SLUGS.has(slug)) {
    result = fromQuery(fetched, (d) => d);
    refetch = () => void fetched.refetch();
  } else {
    result = { status: 'error', permissions: {} };
  }

  // Remotes re-publish into the shared store on a live `permission_changed`; when that
  // disagrees with this map on one of the app's rail keys, re-ask the endpoint.
  const storePermissions = usePermissionsStore((s) => s.permissions);
  const latest = useRef({ railKeys, result, refetch });
  useEffect(() => {
    latest.current = { railKeys, result, refetch };
  });
  useEffect(() => {
    const { railKeys: keys, result: current, refetch: refresh } = latest.current;
    if (current.status !== 'ready') return;
    const diverged = keys.some(
      (k) => typeof storePermissions[k] === 'boolean' && storePermissions[k] !== current.permissions[k],
    );
    if (diverged) refresh();
  }, [storePermissions]);

  return result;
}
